/* =========================================================================
   Monastery EXPANSION WINGS — the big aspirational money sink
   (PLAN-gameplay-v2 §1, db/047).

   The gallery pieces (monastery.ts) are the small, frequent restorations. This
   is the tier above them: six structural wings from $25,000 to $250,000 that
   must be raised IN ORDER, so a six-figure wallet finally has a destination and
   a horizon past it. $650,000 completes the monastery.

   Server-authoritative, without exception:
     • prices live in app.MonasteryWings — everything here is display only,
     • POST /api/monastery/build sends a KEY, never an amount,
     • the debit happens inside app.usp_Ledger_Post under UPDLOCK, idempotently,
     • the ladder's unlock rule is decided by the proc (`unlocked`); the pure
       helpers below only mirror it so the UI can be rendered before/without a
       round trip. The server always gets the last word.

   Guests never see a fake balance: the wallet is accounts-only, so the CTA is
   an honest sign-in nudge (same rule as the gallery grid).

   CSS prefix: `wing-` (grepped clean before use — see the rp-/rpk- collision
   that once made a whole overlay invisible).
   ========================================================================= */
import { esc, fmtCents } from './engine';
import { icon } from './ui/icons';
import { track } from './telemetry';
import { triggerCharPop } from './characters/ui';
import { showRegister } from './auth';
import { byId } from './dom';
import { isAuthenticated, getMonasteryWings, buildMonasteryWing, type MonasteryWing } from './api';

/* ------------------------------- pure logic ------------------------------- */

/** What the player can do with a wing right now. */
export type WingState = 'built' | 'ready' | 'short' | 'locked';

/**
 * PURE. The wing's state for one wallet balance.
 *   built  — already raised
 *   locked — an earlier wing in the ladder is still unbuilt
 *   ready  — next in line and affordable
 *   short  — next in line but the wallet doesn't reach yet
 * The server decides `unlocked`; this only reads it.
 */
export function wingState(wing: MonasteryWing, walletCents: number | null): WingState {
  if (wing.built) return 'built';
  if (!wing.unlocked) return 'locked';
  if (walletCents == null || walletCents < wing.costCents) return 'short';
  return 'ready';
}

/** PURE. Cents still needed for this wing (0 once affordable). */
export function shortfallCents(wing: MonasteryWing, walletCents: number | null): number {
  const have = walletCents == null ? 0 : walletCents;
  return Math.max(0, wing.costCents - have);
}

/** PURE. The one wing the player is working toward: the first unbuilt rung.
    null once the monastery is complete. */
export function nextWing(wings: MonasteryWing[]): MonasteryWing | null {
  const sorted = [...wings].sort((a, b) => a.sortOrder - b.sortOrder);
  return sorted.find((w) => !w.built) ?? null;
}

export interface WingsProgress {
  builtCount: number;
  totalCount: number;
  investedCents: number;
  totalCostCents: number;
  /** 0–100, by COST not by count — the basilica should feel like it moved the bar. */
  pct: number;
}

/** PURE. The header summary. Weighted by cost so the ladder's late, expensive
    rungs visibly dominate the progress bar. */
export function wingsProgress(wings: MonasteryWing[]): WingsProgress {
  let invested = 0;
  let total = 0;
  let built = 0;
  for (const w of wings) {
    total += w.costCents;
    if (w.built) {
      invested += w.costCents;
      built++;
    }
  }
  return {
    builtCount: built,
    totalCount: wings.length,
    investedCents: invested,
    totalCostCents: total,
    pct: total > 0 ? Math.round((invested / total) * 100) : 0,
  };
}

/** PURE. The button/label copy for a wing. Kept out of the render so the tone
    is unit-testable — "$18,400 more to go" must never read as a scold. */
export function wingCta(wing: MonasteryWing, walletCents: number | null, authed: boolean): string {
  if (wing.built) return '✓ Raised';
  if (!authed) return `${fmtCents(wing.costCents)} · Sign in to build`;
  const state = wingState(wing, walletCents);
  if (state === 'locked') return `${fmtCents(wing.costCents)} · Awaits the wing before it`;
  if (state === 'short') return `${fmtCents(shortfallCents(wing, walletCents))} more to go`;
  return `Build · ${fmtCents(wing.costCents)}`;
}

/* -------------------------------- state -------------------------------- */

let wings: MonasteryWing[] = [];
let walletCents: number | null = null;
let loaded = false;
let busy = false;

/** The last-known wallet, for the sibling gallery grid to reuse. */
export function wingsWalletCents(): number | null {
  return walletCents;
}

/** Test seam + a way for monastery.ts to share the wallet it already fetched. */
export function seedWingsWallet(cents: number | null): void {
  if (cents != null) walletCents = cents;
}

/* ------------------------------- rendering ------------------------------- */

function wingCard(w: MonasteryWing): string {
  const state = wingState(w, walletCents);
  const authed = isAuthenticated();
  const cta = wingCta(w, walletCents, authed);
  const ic = w.icon ? esc(w.icon) : icon('church', 22);
  const canPress = authed && state === 'ready';

  const meta =
    state === 'built' && w.builtUtc
      ? `<div class="wing-built-lbl">✓ Raised</div>`
      : `<div class="wing-cost">${esc(fmtCents(w.costCents))}</div>`;

  const btn =
    state === 'built'
      ? ''
      : `<button class="btn wing-cta${canPress ? '' : ' secondary'}" type="button" data-key="${esc(w.key)}"` +
        `${!authed ? ' data-nudge="1"' : ''}${authed && !canPress ? ' disabled' : ''}>${esc(cta)}</button>`;

  return (
    `<div class="wing-card is-${state}">` +
    `<div class="wing-head"><span class="wing-ic" aria-hidden="true">${ic}</span>` +
    `<div class="wing-name">${esc(w.name)}</div>${meta}</div>` +
    (w.descr ? `<div class="wing-descr">${esc(w.descr)}</div>` : '') +
    btn +
    `</div>`
  );
}

function headerHtml(): string {
  const p = wingsProgress(wings);
  const next = nextWing(wings);
  const caption = next
    ? `Next: ${next.name} · ${fmtCents(next.costCents)}`
    : p.totalCount > 0
      ? 'The monastery stands complete.'
      : '';
  return (
    `<div class="wing-summary">` +
    `<div class="wing-summary-top">` +
    `<span class="wing-summary-title">The Expansion</span>` +
    `<span class="wing-summary-count">${p.builtCount} of ${p.totalCount} wings</span>` +
    `</div>` +
    `<div class="wing-bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${p.pct}"` +
    ` aria-label="${p.pct}% of the monastery expansion built">` +
    `<span class="wing-bar-fill" style="width:${p.pct}%"></span></div>` +
    `<div class="wing-summary-sub">${esc(fmtCents(p.investedCents))} of ${esc(fmtCents(p.totalCostCents))} raised${caption ? ` · ${esc(caption)}` : ''}</div>` +
    `</div>`
  );
}

/** Repaint the wings section into #monWings (a no-op when it isn't mounted). */
export function renderWings(): void {
  const host = document.getElementById('monWings');
  if (!host) return;

  if (!isAuthenticated()) {
    host.innerHTML =
      headerHtmlGuest() + `<div class="wing-grid">${wings.map(wingCard).join('')}</div>`;
  } else if (!loaded && wings.length === 0) {
    host.innerHTML = `<div class="wing-loading">Reading the builder’s plans…</div>`;
  } else {
    host.innerHTML = headerHtml() + `<div class="wing-grid">${wings.map(wingCard).join('')}</div>`;
  }

  host.querySelectorAll<HTMLButtonElement>('.wing-cta').forEach((b) => {
    b.onclick = () => {
      if (b.dataset.nudge) {
        try {
          byId('bankbar').classList.add('hidden');
        } catch {
          /* no shell in tests */
        }
        showRegister();
        return;
      }
      void build(b.dataset.key || '');
    };
  });
}

/** Guests see the whole ladder honestly — prices and all — with no fake wallet. */
function headerHtmlGuest(): string {
  const p = wingsProgress(wings);
  return (
    `<div class="wing-summary">` +
    `<div class="wing-summary-top"><span class="wing-summary-title">The Expansion</span></div>` +
    `<div class="wing-summary-sub">Six wings, ${esc(fmtCents(p.totalCostCents))} to raise them all. ` +
    `Your wallet lives with your account. Create a free one to begin.</div>` +
    `</div>`
  );
}

/* ------------------------------- building ------------------------------- */

function toast(msg: string, kind: 'ok' | 'err' = 'ok'): void {
  const t = document.getElementById('monToast');
  if (!t) return;
  t.textContent = msg;
  t.className = 'acct-toast ' + kind;
  void t.offsetWidth;
  t.classList.add('show');
  setTimeout(() => {
    t.classList.remove('show');
    t.classList.add('hidden');
  }, 3600);
}

async function build(key: string): Promise<void> {
  if (busy) return;
  const wing = wings.find((w) => w.key === key);
  if (!wing) return;
  busy = true;
  try {
    const r = await buildMonasteryWing(key);
    if (!r.ok) {
      toast(r.offline ? 'Offline. Try again when connected.' : r.error || 'Could not raise that wing.', 'err');
      return;
    }
    walletCents = r.data.walletBalanceCents;
    const w = wings.find((x) => x.key === key);
    if (w) w.built = true;
    // The ladder is sequential: raising one wing unlocks exactly the next.
    const after = nextWing(wings);
    if (after) after.unlocked = true;

    if (r.data.newlyBuilt) track('MonasteryWingBuilt', { key, cents: r.data.spentCents });
    renderWings();
    if (r.data.blessing) {
      try {
        triggerCharPop(r.data.blessing);
      } catch {
        /* no DOM */
      }
    }
    toast(`${r.data.name} is raised.`);
  } finally {
    busy = false;
  }
}

/* -------------------------------- loading -------------------------------- */

/** Fetch the ladder. Safe for guests (returns quietly) and offline. */
export async function loadWings(): Promise<void> {
  if (!isAuthenticated()) {
    loaded = true;
    return;
  }
  const r = await getMonasteryWings();
  if (r.ok) {
    wings = r.data.wings;
    walletCents = r.data.walletBalanceCents;
  }
  loaded = true;
}

/** Drop cached state on sign-out so the next account starts clean. */
export function resetWings(): void {
  wings = [];
  walletCents = null;
  loaded = false;
}

/** Test seam: inject a ladder without a network round trip. */
export function __setWingsForTest(next: MonasteryWing[], wallet: number | null): void {
  wings = next;
  walletCents = wallet;
  loaded = true;
}
